import { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarCheck, ArrowsInSimple, Kanban, Coffee, ForkKnife, Moon } from "@phosphor-icons/react";
import ReviewNotifications from "@/components/ReviewNotifications";
import ActionCenter from "@/components/ActionCenter";
import ArrivalsDeparturesDialog from "@/components/ArrivalsDeparturesDialog";
import CurrentOccupancy from "@/components/CurrentOccupancy";
import ShoppingList from "@/components/ShoppingList";
import MealsTodayDialog from "@/components/MealsTodayDialog";
import { DashboardTimeTracker, TeamStatusWidget } from "@/components/DashboardDutyWidgets";
import QuickReceiptScan from "@/components/QuickReceiptScan";

function KPI({ icon: Icon, label, value, hint, testId, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`msb-card relative overflow-hidden ${clickable ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all active:translate-y-0" : ""}`}
      data-testid={testId || `kpi-${label.replace(/\s+/g,'-').toLowerCase()}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e)=>{ if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}>
      <div className="absolute -right-4 -top-4 opacity-[0.06]"><Icon size={110} weight="duotone" /></div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{label}</div>
      <div className="text-3xl font-bold mt-2 tracking-tight" style={{fontFamily:"'Work Sans',sans-serif"}}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      {clickable && <div className="text-[10px] text-primary/70 font-semibold mt-2 uppercase tracking-widest">Details ›</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const [s, setS] = useState(null);
  const [dialogMode, setDialogMode] = useState(null);
  const [mealsOpen, setMealsOpen]   = useState(false);

  useEffect(() => {
    api.get(`/stats/dashboard`).then(r => setS(r.data));
  }, []);

  if (!s) return <div className="text-muted-foreground">Lade Dashboard…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-sm uppercase tracking-widest text-muted-foreground" style={{fontFamily:"'Work Sans',sans-serif"}}>Cockpit</div>
          <h1 className="text-4xl font-bold tracking-tight mt-1" style={{fontFamily:"'Work Sans',sans-serif"}}>Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            {isStaff ? `Willkommen ${user?.name} · Ihre Tagesübersicht` : `Operativer Tagesüberblick · Umsatzzahlen finden Sie in der Wachstums-Zentrale`}
          </p>
          <DashboardTimeTracker />
        </div>
      </div>

      <TeamStatusWidget />

      <QuickReceiptScan />

      <CurrentOccupancy />

      {/* Operative Kacheln direkt unter „Aktuelle Belegung" */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPI icon={CalendarCheck}
             label="Anreisen heute"
             value={s.arrivals_today}
             onClick={()=>setDialogMode("arrivals")}/>
        <KPI icon={ArrowsInSimple}
             label="Abreisen heute"
             value={s.departures_today}
             onClick={()=>setDialogMode("departures")}/>
        <KPI icon={Kanban}
             label="Offene Aufgaben"
             value={s.open_tasks}
             hint="→ zum Aufgaben-Board"
             onClick={()=>{ window.location.href = "/aufgaben"; }}/>
        <VerpflegungKPI stats={s} onClick={()=>setMealsOpen(true)}/>
      </div>

      <ShoppingList />

      <ActionCenter />

      <ReviewNotifications />

      <ArrivalsDeparturesDialog
        mode={dialogMode || "arrivals"}
        open={dialogMode !== null}
        onClose={()=>setDialogMode(null)}/>
      <MealsTodayDialog open={mealsOpen} onClose={()=>setMealsOpen(false)}/>
    </div>
  );
}

function VerpflegungKPI({ stats, onClick }) {
  const meals = stats.meals_today || { breakfast: stats.breakfast_today || 0, lunch: 0, dinner: 0 };
  return (
    <div
      className="msb-card relative overflow-hidden cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all active:translate-y-0"
      data-testid="kpi-verpflegung-heute"
      onClick={onClick}
      role="button" tabIndex={0}
      onKeyDown={(e)=>{ if (e.key==="Enter"||e.key===" ") onClick(); }}>
      <div className="absolute -right-4 -top-4 opacity-[0.06]"><Coffee size={110} weight="duotone"/></div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Verpflegung heute</div>
      <div className="mt-2 space-y-1 text-sm">
        <MealRow icon={Coffee}    color="text-orange-600" label="Frühstück"   value={meals.breakfast}/>
        <MealRow icon={ForkKnife} color="text-amber-600"  label="Mittagessen" value={meals.lunch}/>
        <MealRow icon={Moon}      color="text-indigo-600" label="Abendessen"  value={meals.dinner}/>
      </div>
      <div className="text-[10px] text-primary/70 font-semibold mt-2 uppercase tracking-widest">Details ›</div>
    </div>
  );
}

function MealRow({ icon: Icon, color, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <Icon size={16} weight="duotone" className={color}/>
        {label}
      </span>
      <span className="text-lg font-bold leading-none" style={{fontFamily:"'Work Sans',sans-serif"}}>{value ?? 0}</span>
    </div>
  );
}
